import { useEffect, useState } from 'react';
import { CheckCircle2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import FlowShell from './FlowShell';
import {
  CARD_STYLE, TextField, SelectField, TextareaField, RadioField, CheckboxField,
  NextButton, PrimaryButton, formatServicePrice,
} from './FlowField';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const STEP_LABELS = ['Where You Are', 'Challenges', 'Details', 'Recommendation'];

const SITUATION_OPTIONS = [
  { value: 'employed_change',    label: 'Employed and want to change careers' },
  { value: 'recent_grad',        label: 'Recent graduate looking for first role' },
  { value: 'between_jobs',       label: 'Currently between jobs and looking' },
  { value: 'freelance_permanent',label: 'Freelancer wanting to go permanent' },
  { value: 'returning',          label: 'Returning to work after a career break' },
  { value: 'other',              label: 'Other' },
];

const CHALLENGE_OPTIONS = [
  { value: 'cv_no_response',      label: 'My CV is not getting responses' },
  { value: 'interviews_no_offer', label: 'Getting interviews but not converting to offers' },
  { value: 'where_to_look',       label: 'Not knowing how to find the right roles' },
  { value: 'linkedin_silent',     label: 'My LinkedIn is not attracting attention' },
  { value: 'interview_confidence',label: 'Lack of confidence in interviews' },
  { value: 'no_portfolio',        label: 'No strong portfolio or projects to show' },
  { value: 'not_sure',            label: 'Not sure where the problem is' },
];

const COACH_OPTIONS = [
  { value: 'yes_helped',       label: 'Yes and it helped' },
  { value: 'yes_didnt_work',   label: 'Yes but it did not work' },
  { value: 'tried_alone',      label: 'No but I have tried on my own' },
  { value: 'first_time',       label: 'No — this would be my first time' },
];

const TIMELINE_OPTIONS = [
  { value: 'urgent',   label: 'I need a job as soon as possible' },
  { value: 'three_m',  label: 'Within 3 months' },
  { value: 'six_m',    label: 'Within 6 months' },
  { value: 'exploring',label: 'I am exploring options' },
];

function buildRecommendation(form, pricing) {
  const by = {};
  for (const r of pricing) by[r.id] = r;
  const programme = formatServicePrice(by['launch-full-launch-programme']);

  const chal = form.biggest_challenge || [];
  const situation = form.current_situation;
  const coach = form.worked_with_coach;
  const timeline = form.timeline;

  let title = 'Recommended next step';
  let body = '';

  if (situation === 'employed_change' && chal.includes('cv_no_response')) {
    title = 'Rebuild your CV and LinkedIn for the new target field.';
    body = 'You have real experience — the problem is that your CV and LinkedIn are not translating it into the language of your target field. Hiring managers spend seconds on an application. We will rebuild your profile from the ground up — not just improve it — so it speaks directly to the people you want to work for.';
  } else if (situation === 'recent_grad') {
    title = 'Build your portfolio and your profile in parallel.';
    body = 'Your degree proves you can learn. Now you need proof that you can do. We will build your portfolio, optimise your profile for entry-level roles in your target field, prepare you thoroughly for interviews, and actively help you find and approach the right opportunities. Most graduates we work with receive their first offer within 90 days.';
  } else if (chal.includes('interviews_no_offer')) {
    title = 'Go deep on interview performance.';
    body = 'You are doing something right — you are getting in the room. The issue is what happens once you are there. Interview performance is a learnable skill and we will work on it specifically — mock sessions with your actual target role in mind, detailed feedback, and practice until it becomes natural.';
  } else if (situation === 'between_jobs' && timeline === 'urgent') {
    title = 'Accelerated 30-45 day plan.';
    body = 'With urgency comes focus. We will prioritise your CV and LinkedIn in the first week, identify the best opportunities for your profile immediately, and have you actively interviewing within two to three weeks. Most clients in your situation receive offers within 30 to 45 days when we work together with focus.';
  } else if (coach === 'yes_didnt_work') {
    title = 'An outcome-focused engagement, not an advice package.';
    body = 'We understand the frustration. Many career coaches focus on advice — we focus on outcomes. Our approach is built around the offer, not the process. We work with you until you have the result, not until the coaching package runs out.';
  } else {
    title = 'The Full Launch Programme.';
    body = 'Based on your answers, the Full Launch Programme is the right starting point — profile rebuild, interview preparation, and active placement support, with a 90 day target timeline to offer.';
  }

  return { title, body, cta: `Starting from ${programme}.` };
}

export default function LaunchFlow({ onExit }) {
  const [step, setStep] = useState(1);
  const [pricing, setPricing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '',
    current_situation: '', current_role: '', target_role: '',
    biggest_challenge: [],
    worked_with_coach: '', timeline: '',
    additional_details: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/service-pricing`);
        if (res.ok && !cancelled) setPricing((await res.json()).rows || []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const step1Ready = form.first_name && form.last_name && emailValid
    && form.current_situation && form.current_role && form.target_role;
  const step2Ready = form.biggest_challenge.length > 0 && form.worked_with_coach && form.timeline;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email.trim().toLowerCase(),
          service: 'launch',
          source: 'pricing_questionnaire',
          interested_in: `Acodia Launch · target: ${form.target_role}`,
          answers: { ...form, email: undefined },
        }),
      });
      if (!res.ok) throw new Error(`submit failed: ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      toast.error('Submission failed', { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const rec = buildRecommendation(form, pricing);

  const renderStep1 = () => (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="First Name" value={form.first_name} onChange={(v) => set('first_name', v)} required testId="launch-first" />
        <TextField label="Last Name" value={form.last_name} onChange={(v) => set('last_name', v)} required testId="launch-last" />
      </div>
      <div className="mt-4">
        <TextField label="Email" type="email" value={form.email} onChange={(v) => set('email', v)} required testId="launch-email" />
        {form.email && !emailValid && <p className="font-body text-[11px] mt-1" style={{ color: 'var(--warn)' }}>Please enter a valid email address.</p>}
      </div>
      <div className="mt-4">
        <RadioField label="Current situation" value={form.current_situation} onChange={(v) => set('current_situation', v)} options={SITUATION_OPTIONS} required testId="launch-situation" />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Current role or field" value={form.current_role} onChange={(v) => set('current_role', v)} placeholder="e.g. IT Support, Marketing, Teaching, Student" required testId="launch-current-role" />
        <TextField label="Target role" value={form.target_role} onChange={(v) => set('target_role', v)} placeholder="e.g. Cybersecurity Analyst, Data Analyst, Software Developer, Cloud Engineer" required testId="launch-target-role" />
      </div>
      <NextButton disabled={!step1Ready} onClick={() => setStep(2)} testId="launch-step-1-next" />
    </>
  );

  const renderStep2 = () => (
    <>
      <CheckboxField label="Biggest challenge (select all that apply)" values={form.biggest_challenge} onChange={(v) => set('biggest_challenge', v)} options={CHALLENGE_OPTIONS} required testId="launch-challenges" />
      <div className="mt-4">
        <RadioField label="Worked with a career coach before?" value={form.worked_with_coach} onChange={(v) => set('worked_with_coach', v)} options={COACH_OPTIONS} required testId="launch-coach" />
      </div>
      <div className="mt-4">
        <RadioField label="Timeline" value={form.timeline} onChange={(v) => set('timeline', v)} options={TIMELINE_OPTIONS} required testId="launch-timeline" />
      </div>
      <NextButton disabled={!step2Ready} onClick={() => setStep(3)} testId="launch-step-2-next" />
    </>
  );

  const renderStep3 = () => (
    <>
      <TextareaField
        label="Additional details"
        hint="Tell us about your situation, what you have already tried, your target companies or roles, or anything else that would help us prepare for your consultation."
        value={form.additional_details}
        onChange={(v) => set('additional_details', v)}
        placeholder="Optional — the more context you share, the more useful your consultation will be."
        rows={6}
        testId="launch-details"
      />
      <NextButton onClick={() => setStep(4)} testId="launch-step-3-next" />
    </>
  );

  const renderStep4 = () => {
    if (submitted) {
      return (
        <div className="text-center max-w-xl mx-auto py-6" data-testid="launch-success">
          <div
            className="inline-flex items-center justify-center rounded-full mb-6"
            style={{ width: 64, height: 64, background: 'var(--ok-10)', border: '1px solid var(--ok)', boxShadow: '0 0 40px rgba(74,222,128,0.25)' }}
          >
            <CheckCircle2 size={32} style={{ color: 'var(--ok)' }} />
          </div>
          <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--white)' }}>
            Your details have been received.
          </h2>
          <p className="font-body text-sm" style={{ color: 'var(--tm)' }}>
            Expect a response within one business day. We will be in touch to confirm your consultation time.
          </p>
        </div>
      );
    }
    return (
      <>
        <div className="p-6 rounded-lg mb-6" style={{ ...CARD_STYLE, background: 'var(--a-glow)', border: '1px solid var(--a-border)' }} data-testid="launch-recommendation">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            recommendation
          </p>
          <h3 className="font-display text-xl font-semibold mt-2 mb-3" style={{ color: 'var(--white)' }}>{rec.title}</h3>
          <p className="font-body text-sm mb-4" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>{rec.body}</p>
          <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>{rec.cta}</p>
        </div>
        <PrimaryButton onClick={handleSubmit} disabled={submitting} testId="launch-step-4-submit">
          <Mail size={14} className="mr-2" />
          {submitting ? 'Sending…' : 'Book My Free Career Consultation'}
        </PrimaryButton>
      </>
    );
  };

  return (
    <FlowShell
      kicker="acodia launch"
      title={submitted ? 'Thanks — we will be in touch.' : 'Let us find your path to offer.'}
      stepLabels={STEP_LABELS}
      step={step}
      onBack={() => setStep((s) => Math.max(1, s - 1))}
      onExit={onExit}
    >
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </FlowShell>
  );
}
